// Pure presentation and URL helpers for Conversion Journey (C11 thin).
// Rates and counts arrive from funnel-stats; none are reconstructed here.

import { parseRange, rangeWindow, type Range } from "./onboarding-format";
import type { FunnelJourneyStats, JourneyCutKey, JourneyPath, JourneyQuery } from "./stats-api";

export const JOURNEY_KEYS = {
  path: "j_path",
  funnel: "j_funnel",
  range: "j_range",
  from: "j_from",
  to: "j_to",
  source: "j_source",
  door: "j_door",
  goal: "j_goal",
  device: "j_device",
  language: "j_lang",
  step: "j_step",
  by: "j_by",
} as const;

export const JOURNEY_CUTS: JourneyCutKey[] = ["source", "goal", "device", "door", "language"];

const value = (raw: unknown): string | null => (typeof raw === "string" && raw !== "" ? raw : null);

export function journeyPath(raw: unknown): JourneyPath {
  return raw === "post" ? "post" : "pre";
}

export function defaultJourneyFunnel(path: JourneyPath): string {
  return path === "post" ? "precision-qr" : "all-pre";
}

export const defaultFunnelForPath = defaultJourneyFunnel;

export type JourneyUrlState = {
  path: JourneyPath;
  funnel: string;
  range: Range;
  from: string | null;
  to: string | null;
  source: string | null;
  door: string | null;
  goal: string | null;
  device: string | null;
  language: string | null;
  step: string | null;
  by: JourneyCutKey;
};

export function journeyUrlState(search: Record<string, unknown>): JourneyUrlState {
  const path = journeyPath(search[JOURNEY_KEYS.path]);
  const range = parseRange(search[JOURNEY_KEYS.range] ?? "30");
  const by = JOURNEY_CUTS.includes(search[JOURNEY_KEYS.by] as JourneyCutKey)
    ? (search[JOURNEY_KEYS.by] as JourneyCutKey)
    : "device";
  return {
    path,
    funnel: value(search[JOURNEY_KEYS.funnel]) ?? defaultJourneyFunnel(path),
    range,
    from: value(search[JOURNEY_KEYS.from]),
    to: value(search[JOURNEY_KEYS.to]),
    source: value(search[JOURNEY_KEYS.source]),
    door: value(search[JOURNEY_KEYS.door]),
    goal: value(search[JOURNEY_KEYS.goal]),
    device: value(search[JOURNEY_KEYS.device]),
    language: value(search[JOURNEY_KEYS.language]),
    step: value(search[JOURNEY_KEYS.step]),
    by,
  };
}

export function journeySearchToQuery(
  search: Record<string, unknown>,
  today = new Date(),
): { query: JourneyQuery; range: Range } | { error: string; range: Range } {
  const state = journeyUrlState(search);
  const window = rangeWindow(state.range, today, { from: state.from, to: state.to });
  if ("error" in window) return { error: window.error, range: state.range };
  return {
    range: state.range,
    query: {
      from: window.from,
      to: window.to,
      path: state.path,
      funnel: state.funnel,
      source: state.source,
      door: state.door,
      goal: state.goal,
      device: state.device,
      language: state.language,
    },
  };
}

export function journeyQueryToSearch(query: JourneyQuery, range: Range = "custom"): Record<string, string> {
  const out: Record<string, string> = {
    [JOURNEY_KEYS.path]: query.path,
    [JOURNEY_KEYS.funnel]: query.funnel,
    [JOURNEY_KEYS.range]: range,
  };
  if (range === "custom") {
    out[JOURNEY_KEYS.from] = query.from;
    out[JOURNEY_KEYS.to] = query.to;
  }
  for (const key of JOURNEY_CUTS) {
    const urlKey = key === "language" ? JOURNEY_KEYS.language : JOURNEY_KEYS[key];
    if (query[key]) out[urlKey] = query[key];
  }
  return out;
}

export const queryFromJourneyUrl = journeySearchToQuery;
export const queryToJourneyUrl = journeyQueryToSearch;

/** Switching paths also closes a step and selects that path's defined default funnel. */
export function journeyPathSearch(search: Record<string, unknown>, path: JourneyPath): Record<string, unknown> {
  const next: Record<string, unknown> = { ...search, [JOURNEY_KEYS.path]: path, [JOURNEY_KEYS.funnel]: defaultJourneyFunnel(path) };
  delete next[JOURNEY_KEYS.step];
  return next;
}

const PERCENT = new Intl.NumberFormat("en-GB", { style: "percent", minimumFractionDigits: 1, maximumFractionDigits: 1 });

export function formatJourneyRate(rate: number | null | undefined): string {
  return rate == null ? "—" : PERCENT.format(rate);
}

export const journeyRate = formatJourneyRate;

export function formatJourneySeconds(seconds: number | null | undefined): string {
  return seconds == null ? "—" : `${seconds} s`;
}

export const journeySeconds = formatJourneySeconds;

export function journeyCutLine(cut: Record<JourneyCutKey, string | null>): string | null {
  const parts = JOURNEY_CUTS.flatMap((key) => (cut[key] ? [`${key} ${cut[key]}`] : []));
  return parts.length ? `Cut applied: ${parts.join(", ")}.` : null;
}

export function journeyLowDataLine(people: number | null): string {
  return `${people == null ? "—" : people} people in the window. Rates on under 30 people move a lot; the counts are exact.`;
}

export function journeyIsStale(dataAsOf: string | null, now = new Date()): boolean {
  if (!dataAsOf) return false;
  const timestamp = new Date(dataAsOf).getTime();
  return Number.isFinite(timestamp) && now.getTime() - timestamp > 36 * 60 * 60 * 1000;
}

export const isJourneyStale = journeyIsStale;

export function journeyDay(dataAsOf: string): string {
  const date = new Date(dataAsOf);
  if (Number.isNaN(date.getTime())) return dataAsOf;
  return new Intl.DateTimeFormat("en-GB", { weekday: "short", day: "numeric", month: "short", timeZone: "UTC" }).format(date);
}

export type JourneyState = "loading" | "error" | "unmapped" | "empty" | "ideal";

export function pickJourneyState(
  stats: FunnelJourneyStats | undefined,
  flags: { pending?: boolean; error?: boolean } = {},
): JourneyState {
  if (flags.error) return "error";
  if (!stats || flags.pending) return "loading";
  if (!stats.funnel.mapped) return "unmapped";
  if (stats.people === 0) return "empty";
  return "ideal";
}
